import { JRCSidePanelModal } from '../JRCSidePanelModal/JRCSidePanelModal';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import {
    CalendarEventViewAbstractType,
    CalendarEventWithExtensions,
    JArticleApi,
    JCalendarApi,
    JGlobalApi,
} from 'jamespot-user-api';
import { JRCImg } from '../JRCImg/JRCImg';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { JRCTypography } from '../JRCTypography/JRCTypography';
import { JRCAvatar } from '../JRCAvatar/JRCAvatar';
import { JRCAvatars } from '../JRCAvatars/JRCAvatars';
import { JRCTooltip } from '../JRCTooltip/JRCTooltip';
import { isSameDay } from '../../utils/utils.date';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import { JRCHtml } from '../JRCHtml/JRCHtml';
import { JRCAttachments } from '../JRCAttachments/JRCAttachments';
import { JRCButton } from '../JRCButton/JRCButton';
import { JRCInputTextarea } from '../Form/Input/JRCInputTextarea/JRCInputTextarea';
import { useForm } from 'react-hook-form';
import { JRCInputSelect } from '../Form/Input/JRCSelect/JRCInputSelect';
import { JRCModalForm } from '../JRCModal/templates/JRCModalForm';
import { JRCModal } from '../JRCModal/templates/JRCModal';
import { JRCEventStatusButton, SubscriptionModifiers } from './JRCEventStatusButton';
import { JRCStyledHref } from '../JRCHref/JRCHref';

export type JRCCalendarModalProps = {
    event?: CalendarEventWithExtensions<CalendarEventViewAbstractType, ['socialEventRecord']>;
    open: boolean;
    closeHandler: () => void;
    handler: {
        article: Pick<JArticleApi, 'delete'>;
        calendar: Pick<JCalendarApi, 'reviveSubmit'>;
        global: Pick<JGlobalApi, 'updateAction' | 'removeAction'>;
    };
    onError: (errorMsg?: string) => void;
    onSuccess: (message: string) => void;
    extraComponent?: (
        event: CalendarEventWithExtensions<CalendarEventViewAbstractType, ['socialEventRecord']>,
    ) => ReactNode;
    setSubscriptionModifiers: (fn: (prev: SubscriptionModifiers) => SubscriptionModifiers) => void;
    subscriptionModifiers: SubscriptionModifiers;
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.sm}px;
`;

const SubContainer = styled.div<{ mt: boolean }>`
    ${({ mt, theme }) => (mt ? `margin-top: ${theme.space.md}px;` : '')}
    padding: 0 ${({ theme }) => theme.space.md}px;
`;

const CoverContainer = styled.div``;

const AudienceContainer = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.space.xs}px;
    align-items: center;
    margin-top: ${({ theme }) => theme.space.sm}px;
    margin-bottom: ${({ theme }) => theme.space.md}px;
`;

const Dot = styled.div`
    height: 4px;
    width: 4px;
    border-radius: 100%;
    background-color: ${({ theme }) => theme.color.grey4};
`;

const InfosContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.xs}px;
    margin-bottom: ${({ theme }) => theme.space.md}px;
`;

const InfoContainer = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.space.xs}px;
`;

const Href = styled.a<{ underline?: boolean }>`
    ${({ underline }) => underline !== false && 'text-decoration: underline;'}
    &:hover * {
        cursor: pointer;
        ${({ underline }) => underline === false && 'text-decoration: none;'}
        color: ${({ theme }) => theme.color.primary};
    }
`;

const DescriptionContainer = styled.div`
    color: ${({ theme }) => theme.color.grey5};
    margin-bottom: ${({ theme }) => theme.space.md}px;
`;

const AttachmentsContainer = styled.div`
    margin-bottom: ${({ theme }) => theme.space.md}px;
`;

const ButtonsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.space.xs}px;
    margin-bottom: ${({ theme }) => theme.space.sm}px;
`;

const ReportContainer = styled.div`
    color: ${({ theme }) => theme.color.grey5};
    margin-bottom: ${({ theme }) => theme.space.md}px;
    padding-left: ${({ theme }) => theme.space.md}px;
    border-left: 4px solid ${({ theme }) => theme.color.grey2};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.xs}px;
`;

const CeilingContainer = styled.div`
    margin-right: ${({ theme }) => theme.space.xs}px;
    margin-bottom: ${({ theme }) => theme.space.xs}px;
`;

type ReviveForm = Pick<Parameters<JCalendarApi['reviveSubmit']>[0], 'list' | 'text' | 'userURI'>;

export const JRCCalendarModal = ({
    open,
    closeHandler,
    event,
    handler,
    onError,
    onSuccess,
    extraComponent,
    setSubscriptionModifiers,
    subscriptionModifiers,
}: JRCCalendarModalProps) => {
    const intl = useIntl();
    const { control, handleSubmit, watch, reset } = useForm<ReviveForm>({
        defaultValues: {
            list: 'all',
            text: '',
        },
    });
    const [isReviveModalOpen, setIsReviveModalOpen] = useState(false);
    const [isReviveLoading, setIsReviveLoading] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const reviveOptions = useMemo(() => {
        const options = [
            {
                label: intl.formatMessage({ id: 'CALENDAR_Event_Revive_All' }),
                value: 'all',
            },
            {
                label: intl.formatMessage({ id: 'CALENDAR_Event_Revive_Available' }),
                value: 'available',
            },
            {
                label: intl.formatMessage({ id: 'CALENDAR_Event_Revive_Unavailable' }),
                value: 'unavailable',
            },
            {
                label: intl.formatMessage({ id: 'CALENDAR_Event_Revive_Waiting' }),
                value: 'waiting',
            },
        ];
        if (event?._extend?.socialEventRecord?.isVisio) {
            options.push({
                label: intl.formatMessage({ id: 'CALENDAR_Event_Revive_On_Spot' }),
                value: 'onSpot',
            });
            options.push({
                label: intl.formatMessage({ id: 'CALENDAR_Event_Revive_Visio' }),
                value: 'visio',
            });
        }
        return options;
    }, [event?._extend?.socialEventRecord?.isVisio, intl]);

    const title = useMemo(() => {
        switch (event?.type) {
            case 'socialEvent':
            default:
                return intl.formatMessage({ id: 'CALENDAR_Event' });
            case 'meeting':
                return intl.formatMessage({ id: 'CALENDAR_Meeting' });
            case 'animatedMeeting':
                return intl.formatMessage({ id: 'GLOBAL_Animated_Meeting' });
            case 'boardCard':
                return intl.formatMessage({ id: 'CALENDAR_BoardCard' });
        }
    }, [event?.type, intl]);

    const reviveEvent = useCallback(
        (data: ReviveForm) => {
            if (!event) return;
            if (!data.list || !data.text) return;
            setIsReviveLoading(true);
            handler.calendar
                .reviveSubmit({
                    list: data.list,
                    text: data.text,
                    idArticle: event.id,
                })
                .then(() => {
                    setIsReviveModalOpen(false);
                    reset();
                    onSuccess(intl.formatMessage({ id: 'GLOBAL_Sent' }));
                })
                .catch((e) => {
                    onError(e.errorMsg);
                })
                .finally(() => {
                    setIsReviveLoading(false);
                });
        },
        [event, handler.calendar, intl, onError, onSuccess, reset],
    );

    const formattedDate = useMemo(() => {
        if (!event) return '';
        if (event.dateStart && event.dateEnd) {
            return intl.formatMessage(
                {
                    id:
                        (isSameDay(event.dateStart, event.dateEnd)
                            ? 'CALENDAR_Event_Date_Start_End_Same_Day'
                            : 'CALENDAR_Event_Date_Start_End') + (event.allDay ? '_All_Day' : ''),
                },
                {
                    dateStart: intl.formatDate(new Date(event.dateStart), {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                    }),
                    timeStart: intl.formatTime(new Date(event.dateStart), {
                        format: 'hh:mm',
                    }),
                    dateEnd: intl.formatDate(new Date(event.dateEnd), {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                    }),
                    timeEnd: intl.formatTime(new Date(event.dateEnd), {
                        format: 'hh:mm',
                    }),
                },
            );
        } else {
            return intl.formatMessage(
                { id: 'CALENDAR_Event_Date' + (event.allDay ? '_All_Day' : '') },
                {
                    date: intl.formatDate(new Date(event.dateStart ?? event.dateEnd ?? ''), {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                    }),
                    time: intl.formatTime(new Date(event.dateStart ?? event.dateEnd ?? ''), {
                        format: 'hh:mm',
                    }),
                },
            );
        }
    }, [event, intl]);

    const deleteEvent = useCallback(() => {
        if (!event) return;
        setIsDeleteLoading(true);
        handler.article
            .delete(event.id)
            .then(() => {
                location.reload();
            })
            .catch((e) => {
                onError(e.errorMsg);
                setIsDeleteLoading(false);
            });
    }, [event, handler.article, onError]);

    return (
        <>
            <JRCSidePanelModal
                zIndex={10000}
                enableClickAwayCloseModal
                open={open}
                position="right"
                closeHandler={closeHandler}
                title={title}
                showIconClose
                removePadding
                offsetTop={0}
                optionsButton={
                    event && (event._right.update || event._right.delete)
                        ? [
                              ...(event._right.update
                                  ? [
                                        {
                                            label: intl.formatMessage({ id: 'GLOBAL_Edit' }),
                                            href: `/?action=article-edit&idArticle=${event.id}`,
                                        },
                                    ]
                                  : []),
                              ...(event._right.delete
                                  ? [
                                        {
                                            label: intl.formatMessage({ id: 'GLOBAL_Delete' }),
                                            onClick: () => setDeleteModalOpen(true),
                                        },
                                    ]
                                  : []),
                          ]
                        : undefined
                }>
                {event && (
                    <Container>
                        {event.image && (
                            <CoverContainer>
                                <JRCImg
                                    aspectRatio={2}
                                    alt={event.title}
                                    type={event.type}
                                    recordId={event.id}
                                    size={'original'}
                                    from={'imagecache'}
                                    objectFit={'cover'}
                                    width={'100%'}
                                />
                            </CoverContainer>
                        )}
                        <SubContainer mt={!event.image}>
                            <Href href={event._url} underline={false}>
                                <JRCTypography size={'xl'} weight={'semiBold'}>
                                    {event.title}
                                </JRCTypography>
                            </Href>
                            <AudienceContainer>
                                <JRCTooltip description={event._user.title}>
                                    <Href href={event._user._url} aria-label={event._user.title}>
                                        <JRCAvatar uri={event._user.uri} />
                                    </Href>
                                </JRCTooltip>
                                {event._audience.length > 0 && (
                                    <>
                                        <Dot />
                                        <JRCAvatars audience={event._audience} />
                                    </>
                                )}
                            </AudienceContainer>
                            <InfosContainer>
                                {(event.dateStart || event.dateEnd) && (
                                    <InfoContainer>
                                        <JRCIcon isMargin={false} name={'icon-calendar'} color={'grey5'} />
                                        <JRCTypography color={'grey5'}>{formattedDate}</JRCTypography>
                                    </InfoContainer>
                                )}
                                {event.address && (
                                    <InfoContainer>
                                        <JRCIcon isMargin={false} name={'icon-location-dot'} color={'grey5'} />
                                        <JRCTypography color={'grey5'}>{event.address}</JRCTypography>
                                    </InfoContainer>
                                )}
                                {event.place && (
                                    <InfoContainer>
                                        <JRCIcon isMargin={false} name={'icon-camera-web'} color={'grey5'} />
                                        <JRCTypography color={'grey5'}>
                                            <Href href={event.place.match(/http.*/)?.[0]} target="_blank">
                                                {intl.formatMessage({ id: 'CALENDAR_Event_Visio_Url' })}
                                            </Href>
                                        </JRCTypography>
                                    </InfoContainer>
                                )}
                            </InfosContainer>
                            {event.description && (
                                <DescriptionContainer>
                                    <JRCHtml __html={event.description} />
                                </DescriptionContainer>
                            )}
                            {event.agenda && (
                                <DescriptionContainer>
                                    <JRCHtml __html={event.agenda} />
                                </DescriptionContainer>
                            )}
                            <AttachmentsContainer>
                                {event._attachedFiles.length > 0 && <JRCAttachments files={event._attachedFiles} />}
                            </AttachmentsContainer>
                            {event.report && (
                                <ReportContainer>
                                    <JRCTypography size={'s'} weight={'medium'} color={'grey6'}>
                                        {intl.formatMessage({ id: 'CALENDAR_Event_Report' })}
                                    </JRCTypography>
                                    <JRCHtml __html={event.report} />
                                </ReportContainer>
                            )}
                            {event.socialEventCeiling !== undefined &&
                                event.socialEventCeiling !== null &&
                                event.socialEventUseCeiling &&
                                event._extend?.socialEventRecord && (
                                    <CeilingContainer>
                                        <JRCTypography size={'s'} color={'grey4'} textAlign={'right'}>
                                            {event.socialEventCeiling -
                                                (event._extend.socialEventRecord.subscribersTotal +
                                                    (subscriptionModifiers.find((s) => s.uri === event.uri)?.modifier ??
                                                        0))}
                                            /{event.socialEventCeiling}{' '}
                                            {intl.formatMessage({ id: 'CALENDAR_Event_Remaining_Seats' })}
                                        </JRCTypography>
                                    </CeilingContainer>
                                )}
                            <ButtonsContainer>
                                {event._right.update && !event.noGestion && (
                                    <JRCButton
                                        color={'cancel'}
                                        onClick={() => setIsReviveModalOpen(true)}
                                        label={intl.formatMessage({ id: 'CALENDAR_Event_Revive' })}
                                    />
                                )}
                                <JRCStyledHref
                                    href={`/?action=ical&id=${event.id}&type=${event.type}`}
                                    as={'button'}
                                    color={'cancel'}>
                                    iCal
                                </JRCStyledHref>
                                {event._extend?.socialEventRecord?.userResponseStatus && !event.noGestion && (
                                    <JRCEventStatusButton
                                        onSuccess={(message) => {
                                            onSuccess(message);
                                        }}
                                        handler={handler}
                                        onError={onError}
                                        eventId={event.id}
                                        eventType={event.type}
                                        initialStatus={event._extend.socialEventRecord.userResponseStatus}
                                        eventIsVisio={event._extend.socialEventRecord.isVisio}
                                        canSubscribe={
                                            !event.socialEventUseCeiling ||
                                            event.socialEventCeiling === null ||
                                            event._extend.socialEventRecord.subscribersTotal +
                                                (subscriptionModifiers.find((s) => s.uri === event.uri)?.modifier ??
                                                    0) <
                                                (event.socialEventCeiling ?? -1)
                                        }
                                        setSubscriptionModifiers={setSubscriptionModifiers}
                                        eventUri={event.uri}
                                        status={
                                            subscriptionModifiers.find((s) => s.uri === event.uri)?.status ??
                                            event._extend.socialEventRecord.userResponseStatus
                                        }
                                    />
                                )}
                            </ButtonsContainer>
                            {extraComponent && extraComponent(event)}
                        </SubContainer>
                    </Container>
                )}
            </JRCSidePanelModal>
            <JRCModalForm
                maxHeight={'550px'}
                open={isReviveModalOpen}
                closeHandler={() => {
                    setIsReviveModalOpen(false);
                    reset();
                }}
                title={intl.formatMessage({ id: 'CALENDAR_Event_Revive' })}
                buttons={[
                    {
                        children: intl.formatMessage({ id: 'GLOBAL_Cancel' }),
                        color: 'cancel',
                        clickHandler: () => {
                            setIsReviveModalOpen(false);
                            reset();
                        },
                    },
                    {
                        children: intl.formatMessage({ id: 'CALENDAR_Event_Revive' }),
                        color: 'primary',
                        loading: isReviveLoading,
                        clickHandler: handleSubmit(reviveEvent),
                        disabled: !watch('list') || !watch('text'),
                    },
                ]}
                onSubmit={handleSubmit(reviveEvent)}>
                <JRCInputSelect
                    description={intl.formatMessage({ id: 'CALENDAR_Event_Revive_Select_Description' })}
                    control={control}
                    name={'list'}
                    options={reviveOptions}
                    rules={{ required: true }}
                />
                <JRCInputTextarea
                    label={intl.formatMessage({ id: 'CALENDAR_Event_Revive_Message_Label' })}
                    description={intl.formatMessage({ id: 'CALENDAR_Event_Revive_Message_Description' })}
                    control={control}
                    name={'text'}
                    rules={{ required: true }}
                />
            </JRCModalForm>
            <JRCModal
                open={isDeleteModalOpen}
                closeHandler={() => setDeleteModalOpen(false)}
                title={intl.formatMessage({ id: 'GLOBAL_Delete' })}
                buttons={[
                    {
                        color: 'cancel',
                        clickHandler: () => setDeleteModalOpen(false),
                        children: intl.formatMessage({ id: 'GLOBAL_Cancel' }),
                    },
                    {
                        color: 'danger',
                        clickHandler: deleteEvent,
                        loading: isDeleteLoading,
                        children: intl.formatMessage({ id: 'GLOBAL_Delete' }),
                    },
                ]}>
                <JRCTypography>{intl.formatMessage({ id: 'GLOBAL_Delete_Confirmation' })}</JRCTypography>
            </JRCModal>
        </>
    );
};
